import React, { useState, useEffect } from "react";
import { Warning, Phone, Receipt, Camera, PaperPlaneRight } from "@phosphor-icons/react";
import { api, formatDate, formatCurrency } from "../../utils/api";
import { Badge } from "../shared/Badge";
import { Button } from "../shared/Button";
import { Loading, EmptyState } from "../shared/EmptyState";
import { Modal } from "../shared/Modal";

const REMITENTE_LABEL = {
  cliente: null,
  secretaria: "Secretaria (IA)",
  andy: "Tú",
};

const ETIQUETA_INFO = {
  caliente: { emoji: "🔥", label: "Caliente" },
  tibio: { emoji: "🌤️", label: "Tibio" },
  frio: { emoji: "🧊", label: "Frío" },
};

export const WhatsAppInbox = ({ adminToken }) => {
  const [conversaciones, setConversaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("todos");
  const [seleccionada, setSeleccionada] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [mensajeNuevo, setMensajeNuevo] = useState("");
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    cargar();
  }, []);

  const cargar = async () => {
    try {
      const data = await api.get("/whatsapp/conversaciones", adminToken);
      setConversaciones(data);
    } catch (e) {
      console.error("Error:", e);
    } finally {
      setLoading(false);
    }
  };

  const abrirDetalle = async (conv) => {
    setSeleccionada(conv);
    setCargandoDetalle(true);
    try {
      const data = await api.get(`/whatsapp/conversaciones/${conv.wa_id}`, adminToken);
      setDetalle(data);
    } catch (e) {
      console.error("Error:", e);
    } finally {
      setCargandoDetalle(false);
    }
  };

  const recargarDetalle = async (waId) => {
    const data = await api.get(`/whatsapp/conversaciones/${waId}`, adminToken);
    setDetalle(data);
  };

  const enviarRespuesta = async () => {
    if (!mensajeNuevo.trim() || !seleccionada || enviando) return;
    setEnviando(true);
    try {
      await api.post(`/whatsapp/conversaciones/${seleccionada.wa_id}/responder`, { texto: mensajeNuevo.trim() }, adminToken);
      setMensajeNuevo("");
      await recargarDetalle(seleccionada.wa_id);
      cargar();
    } catch (e) {
      alert("No se pudo enviar el mensaje. Probá de nuevo en un momento.");
    } finally {
      setEnviando(false);
    }
  };

  const enviarLinkApp = async () => {
    if (!seleccionada || enviando) return;
    setEnviando(true);
    try {
      await api.post(`/whatsapp/conversaciones/${seleccionada.wa_id}/enviar-app`, {}, adminToken);
      await recargarDetalle(seleccionada.wa_id);
      cargar();
    } catch (e) {
      alert("No se pudo mandar el link de la app. Probá de nuevo en un momento.");
    } finally {
      setEnviando(false);
    }
  };

  const enviarVideollamada = async () => {
    if (!seleccionada || enviando) return;
    setEnviando(true);
    try {
      await api.post(`/whatsapp/conversaciones/${seleccionada.wa_id}/videollamada`, {}, adminToken);
      await recargarDetalle(seleccionada.wa_id);
      cargar();
    } catch (e) {
      alert("No se pudo mandar el link de videollamada. Probá de nuevo en un momento.");
    } finally {
      setEnviando(false);
    }
  };

  const marcarResuelta = async () => {
    if (!seleccionada || enviando) return;
    setEnviando(true);
    try {
      await api.post(`/whatsapp/conversaciones/${seleccionada.wa_id}/resolver`, {}, adminToken);
      await recargarDetalle(seleccionada.wa_id);
      cargar();
    } catch (e) {
      alert("No se pudo marcar como resuelta. Probá de nuevo en un momento.");
    } finally {
      setEnviando(false);
    }
  };

  const marcarTrabajoTerminado = async () => {
    if (!seleccionada || enviando) return;
    if (!window.confirm("¿Marcar el trabajo de esta conversación como terminado? La próxima vez que el cliente escriba, la secretaria lo va a saludar como si fuera un asunto nuevo.")) return;
    setEnviando(true);
    try {
      await api.post(`/whatsapp/conversaciones/${seleccionada.wa_id}/trabajo-terminado`, {}, adminToken);
      await recargarDetalle(seleccionada.wa_id);
      cargar();
    } catch (e) {
      alert("No se pudo marcar el trabajo como terminado. Probá de nuevo en un momento.");
    } finally {
      setEnviando(false);
    }
  };

  const conversacionesFiltradas = conversaciones.filter((c) => {
    if (filtro === "todos") return true;
    if (filtro === "urgente") return c.urgente;
    if (filtro === "handoff") return c.handoff_solicitado;
    return c.etiqueta === filtro;
  });

  if (loading) return <Loading />;

  const urgentes = conversaciones.filter((c) => c.urgente).length;
  const handoffs = conversaciones.filter((c) => c.handoff_solicitado).length;

  return (
    <div className="pb-24">
      <div className="container pt-4">
        <div className="mb-4">
          <h1 className="text-h2 font-display mb-1">💬 WhatsApp</h1>
          <p className="text-small text-secondary">
            {conversaciones.length} conversaciones · {urgentes} urgentes · {handoffs} piden hablar con vos
          </p>
        </div>

        <div className="tabs mb-4">
          {[
            { id: "todos", label: "Todos" },
            { id: "urgente", label: "🚨 Urgentes" },
            { id: "handoff", label: "📞 Piden hablar" },
            { id: "caliente", label: "🔥 Calientes" },
            { id: "tibio", label: "🌤️ Tibios" },
            { id: "frio", label: "🧊 Fríos" },
          ].map((f) => (
            <button key={f.id} onClick={() => setFiltro(f.id)} className={`tab ${filtro === f.id ? "tab-active" : ""}`}>
              {f.label}
            </button>
          ))}
        </div>

        {conversacionesFiltradas.length === 0 ? (
          <EmptyState icon="💬" title="Sin conversaciones" description="Acá van a aparecer los chats reales de la secretaria de WhatsApp con tus clientes" />
        ) : (
          <div className="space-y-3">
            {conversacionesFiltradas.map((c) => (
              <div key={c.wa_id} className="card p-4 cursor-pointer card-interactive" onClick={() => abrirDetalle(c)}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="font-bold text-small">{c.nombre_perfil || c.wa_id}</div>
                    <div className="text-tiny text-secondary">{c.wa_id}</div>
                  </div>
                  <div className="flex gap-1 flex-wrap justify-end">
                    {c.urgente && <Badge variant="error">🚨 Urgente</Badge>}
                    {c.handoff_solicitado && <Badge variant="warning">📞 Pide hablar</Badge>}
                    {c.etiqueta && ETIQUETA_INFO[c.etiqueta] && (
                      <Badge variant="outline">
                        {ETIQUETA_INFO[c.etiqueta].emoji} {ETIQUETA_INFO[c.etiqueta].label}
                      </Badge>
                    )}
                  </div>
                </div>
                {c.ultimo_mensaje && (
                  <div className="text-tiny text-secondary" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {c.ultimo_mensaje.de === "cliente" ? "Cliente: " : c.ultimo_mensaje.de === "andy" ? "Tú: " : "Secretaria: "}
                    {c.ultimo_mensaje.texto}
                  </div>
                )}
                <div className="flex items-center justify-between mt-2">
                  <div className="text-tiny text-secondary">{formatDate(c.actualizado_en)}</div>
                  {c.presupuesto_relacionado && (
                    <div className="flex items-center gap-1 text-tiny" style={{ color: "var(--brand-accent)" }}>
                      <Receipt size={12} /> {c.presupuesto_relacionado.numero} · {formatCurrency(c.presupuesto_relacionado.total)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal isOpen={!!seleccionada} onClose={() => { setSeleccionada(null); setDetalle(null); }} title={seleccionada?.nombre_perfil || seleccionada?.wa_id || ""}>
        {cargandoDetalle ? (
          <Loading />
        ) : detalle ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-tiny text-secondary">
                <Phone size={14} /> {detalle.wa_id}
              </div>
              {detalle.cerrado_en ? (
                <span className="text-tiny" style={{ color: "var(--success)" }}>✅ Trabajo marcado como terminado</span>
              ) : (
                <button className="text-tiny" style={{ textDecoration: "underline", color: "var(--text-secondary)" }} onClick={marcarTrabajoTerminado} disabled={enviando}>
                  Marcar trabajo terminado
                </button>
              )}
            </div>

            {detalle.presupuesto_relacionado && (
              <div className="card p-3" style={{ background: "var(--surface-secondary)" }}>
                <div className="flex items-center gap-2 text-small font-bold">
                  <Receipt size={16} /> Presupuesto {detalle.presupuesto_relacionado.numero}
                </div>
                <div className="text-tiny text-secondary mt-1">
                  Estado: {detalle.presupuesto_relacionado.estado} · {formatCurrency(detalle.presupuesto_relacionado.total)}
                </div>
              </div>
            )}

            {(detalle.urgente || detalle.handoff_solicitado) && (
              <div className="card p-3" style={{ background: "rgba(193, 41, 46, 0.15)", color: "var(--error)" }}>
                <div className="flex items-center gap-2">
                  <Warning size={18} />
                  <span className="text-small font-bold">
                    {detalle.urgente ? "Marcado como urgente" : "Pidió hablar con vos directamente"}
                  </span>
                </div>
                <Button variant="outline" size="sm" className="mt-2" onClick={marcarResuelta} disabled={enviando}>
                  Marcar como resuelta
                </Button>
              </div>
            )}

            <div className="space-y-3" style={{ maxHeight: "40vh", overflowY: "auto" }}>
              {(detalle.mensajes || []).map((m, i) => (
                <div key={i} style={{ display: "flex", justifyContent: m.de === "cliente" ? "flex-start" : "flex-end" }}>
                  <div
                    className="p-3 rounded-lg text-small"
                    style={{
                      maxWidth: "80%",
                      background: m.de === "cliente" ? "var(--surface-secondary)" : "var(--brand-accent)",
                      color: m.de === "cliente" ? "inherit" : "#fff",
                    }}
                  >
                    {REMITENTE_LABEL[m.de] && (
                      <div className="text-tiny font-bold" style={{ opacity: 0.85, marginBottom: 2 }}>
                        {REMITENTE_LABEL[m.de]}
                      </div>
                    )}
                    {m.imagen && (
                      <img src={m.imagen} alt="Foto del cliente" style={{ maxWidth: "100%", borderRadius: 8, marginBottom: 6 }} />
                    )}
                    <div>{m.texto}</div>
                    <div className="text-tiny" style={{ opacity: 0.7, marginTop: 4 }}>{formatDate(m.en)}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="pt-3 border-t border-subtle space-y-2">
              <div className="flex gap-2">
                <input
                  className="input"
                  style={{ flex: 1 }}
                  placeholder="Escribí tu respuesta..."
                  value={mensajeNuevo}
                  onChange={(e) => setMensajeNuevo(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      enviarRespuesta();
                    }
                  }}
                  disabled={enviando}
                />
                <Button
                  variant="accent"
                  onClick={enviarRespuesta}
                  disabled={!mensajeNuevo.trim() || enviando}
                  icon={<PaperPlaneRight size={18} />}
                />
              </div>
              <Button variant="outline" className="btn-full" onClick={enviarLinkApp} disabled={enviando}>
                Enviar app al cliente
              </Button>
              <Button variant="outline" className="btn-full" onClick={enviarVideollamada} disabled={enviando} icon={<Camera size={18} />}>
                Mandar link de videollamada
              </Button>
              <div className="text-tiny text-secondary">
                Tus respuestas y el link de videollamada se mandan desde el mismo WhatsApp del negocio — el cliente no ve ninguna diferencia con la secretaria.
              </div>
            </div>
          </div>
        ) : (
          <EmptyState icon="💬" title="No se pudo cargar" description="Probá cerrar y abrir de nuevo" />
        )}
      </Modal>
    </div>
  );
};
